'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Nail Dust Is Microplastic — and Nail Polish Has a Legal Exemption Until 2035"
      author="MicroPlastics Research Desk"
      date="August 12, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Do gel and acrylic nails shed microplastics: acrylic is the dominant polymer in nail salon air, and EU rules give nail products until October 2035"
    >
      <BlogLastUpdated iso="2026-08-12" formatted="August 12, 2026" />

      <BlogTLDR>
        <strong>Yes, and the dust is the part with a number attached to it.</strong>{' '}
        There is a trend of people keeping the fine powder from an acrylic fill
        in a jar as a souvenir, and the correction going round with it is
        chemically right: that powder is polymerised acrylic, which is to say it
        is microplastic. The only peer-reviewed measurement of nail salon air
        found <strong>46 &plusmn; 55 microplastic particles per cubic
        metre</strong>, an estimated <strong>67,567 inhaled particles a
        year</strong>, mostly fragments under 50 micrometres, and{' '}
        <strong>acrylic as the most common polymer at 27%.</strong> Then
        the part nobody has told you: under the EU microplastics restriction,{' '}
        <strong>nail products got the longest exemption of any category in the
        entire regulation — 17 October 2035.</strong> Face scrubs had to
        reformulate by 2027. Moisturiser by 2029. Nail polish gets twelve years,
        and from 17 October 2031 it has to carry the words{' '}
        <em>&ldquo;This product contains microplastics.&rdquo;</em>
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Acrylic nail dust is microplastic by definition</strong> — polymerised acrylic ground into fragments. It is not a debatable framing.</>,
          <>Nail salon air measured <strong>46 &plusmn; 55 MPs/m&sup3;</strong>, with an estimated <strong>67,567 &plusmn; 81,782 particles inhaled per year.</strong></>,
          <><strong>Acrylic was the top polymer at 27%</strong>, ahead of rubber at 21% and polyurethane at 13%. Most particles were fragments <strong>under 50 &micro;m</strong>, which is the respirable range.</>,
          <><strong>Air conditioning made it worse, not better</strong>, along with plastic ceilings and flooring and the number of people in the room.</>,
          <><strong>Nail products have until 17 October 2035</strong> under Regulation (EU) 2023/2055 — the longest transition of any category. Rinse-off got 2027, leave-on 2029.</>,
          <>From <strong>17 October 2031</strong>, nail, lip and make-up products sold in the EU with intentionally added microplastics must state <strong>&ldquo;This product contains microplastics.&rdquo;</strong></>,
          <><strong>The technician is the exposed party, not the client.</strong> You sit in that air for two hours a month. They breathe it for forty hours a week.</>,
        ]}
      />

      <KeyStats
        heading="The measured numbers"
        stats={[
          { value: '46 ± 55', label: 'microplastics per m³ of nail salon air', context: 'higher than the other indoor environments the same study compared against', source: 'Chen et al., Science of the Total Environment, 2022', sourceUrl: 'https://doi.org/10.1016/j.scitotenv.2021.151472' },
          { value: '67,567', label: 'particles inhaled per year, estimated', context: '± 81,782 — the spread is enormous because salons differ wildly', source: 'Chen et al., Science of the Total Environment, 2022', sourceUrl: 'https://doi.org/10.1016/j.scitotenv.2021.151472' },
          { value: '27%', label: 'of airborne particles were acrylic', context: 'the single dominant polymer, ahead of rubber (21%) and polyurethane (13%)', source: 'Chen et al., Science of the Total Environment, 2022', sourceUrl: 'https://doi.org/10.1016/j.scitotenv.2021.151472' },
          { value: '17 Oct 2035', label: 'EU deadline for nail products', context: 'against 17 Oct 2027 for rinse-off and 17 Oct 2029 for leave-on cosmetics', source: 'Regulation (EU) 2023/2055, REACH Annex XVII entry 78', sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
          { value: '17 Oct 2031', label: 'when the warning label starts', context: '"This product contains microplastics", required on nail, lip and make-up products until the 2035 cut-off', source: 'Regulation (EU) 2023/2055, REACH Annex XVII entry 78', sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
        ]}
      />

      <h2>The jar of dust</h2>
      <p>
        The trend is small and strangely sweet: keep the powder that comes off
        under the e-file during a fill, save it in a jar, and think of it as
        something you have made rather than something you have thrown away. The
        correction that went round with it was blunt and correct.{' '}
        <strong>That powder is not nail. It is polymerised acrylic, shaved into
        fragments by a rotating bit at fifteen to thirty thousand
        revolutions per minute.</strong>
      </p>
      <p>
        Acrylic nails are made by mixing a liquid monomer, usually ethyl
        methacrylate, with a fine polymer powder — polymethyl methacrylate, the
        same material as Perspex and Plexiglass — which then hardens into a
        rigid plastic shell on the nail plate. When you file that shell down,
        you are not removing a coating. You are machining a plastic, and the
        swarf goes into the air of the room.
      </p>
      <p>
        This is one of the rare cases where a viral claim is understating
        things, because the size distribution is the part that actually matters
        and no one mentions it.{' '}
        <strong>The measured particles were predominantly fragments smaller
        than 50 micrometres.</strong> That is small enough to stay suspended in
        room air rather than settling onto the table, and small enough to be
        inhaled rather than caught in the nose. A jar on a shelf is the visible,
        harmless fraction. The fraction that matters is the one you cannot see.
      </p>

      <InlineGuessReveal
        source="nail_salon_air_polymer"
        question="What was the most common polymer measured in nail salon air?"
        options={[
          { label: 'Polyester, from clothing fibres' },
          { label: 'Acrylic, from nail products', correct: true },
          { label: 'Polyethylene, from packaging' },
          { label: 'Nylon, from towels and gowns' },
        ]}
        answer="Acrylic — 27% of all particles"
        answerNote={
          <>
            In most indoor environments ever sampled, polyester fibres from
            clothing and soft furnishings dominate. Nail salons are one of the
            few rooms where they do not. Acrylic came first at 27%, then rubber
            at 21% and polyurethane at 13% — a fingerprint that points straight
            at the treatments themselves rather than at the building.
          </>
        }
        ctaText="Acrylates hide behind a dozen INCI names in the beauty aisle."
        ctaLabel="Scan a nail product"
      />

      <RiskQuizInlineNudge source="blog_inline_nail_microplastics" />

      <h2>What each type of manicure actually is</h2>
      <p>
        &ldquo;Gel&rdquo;, &ldquo;acrylic&rdquo;, &ldquo;dip&rdquo; and{' '}
        &ldquo;press-on&rdquo; sound like four different things. Chemically they
        are four arrangements of the same acrylate family, and the differences
        that matter are how the plastic gets onto the nail and how it comes off.
      </p>

      <BlogComparisonTable
        caption="Manicure types by polymer chemistry, how the plastic is removed, and where the particles end up (checked August 2026)"
        columns={[
          { key: 'type', label: 'Type' },
          { key: 'chem', label: 'What it is' },
          { key: 'removal', label: 'How it comes off' },
          { key: 'where', label: 'Where the plastic goes' },
        ]}
        rows={[
          { type: 'Acrylic extensions', chem: 'Liquid ethyl methacrylate monomer cured with PMMA powder', removal: 'E-file, then soak', where: 'Airborne dust — the highest particle route of any manicure' },
          { type: 'Hard gel / builder gel', chem: 'Urethane acrylate oligomers cured under UV or LED', removal: 'E-file — it does not dissolve', where: 'Airborne dust' },
          { type: 'Soft gel / gel polish', chem: 'Acrylate oligomers plus a photoinitiator, UV cured', removal: 'Acetone soak, then scrape', where: 'Flakes into a bowl and down the drain' },
          { type: 'Dip powder', chem: 'PMMA powder set with a cyanoacrylate resin', removal: 'Acetone soak or e-file', where: 'Both routes, plus airborne powder during application' },
          { type: 'Press-ons', chem: 'ABS historically; premium sets now moulded soft UV gel', removal: 'Lifted off with oil or acetone', where: 'Intact into the bin — the lowest particle route' },
          { type: 'Regular polish', chem: 'Nitrocellulose film with acrylate copolymer film formers, often PET glitter', removal: 'Acetone remover on a cotton pad', where: 'Onto the pad, then the bin' },
        ]}
      />

      <p>
        Read down the last column and an unexpected ranking falls out.{' '}
        <strong>The press-on set — the option that looks the most
        &ldquo;plastic&rdquo; and gets treated as the cheap choice — is the only
        one that comes off in one piece and goes into a bin.</strong> Everything
        removed with a drill becomes respirable dust, and everything removed
        with acetone becomes a slurry of dissolved resin and plastic flakes
        going down a salon sink. Looking like plastic and shedding plastic turn
        out to be nearly opposite properties, which is a pattern we keep running
        into — the same inversion shows up in{' '}
        <Link href="/blog/clean-beauty-microplastics-loophole" className="text-forest underline">the clean beauty loophole</Link>.
      </p>

      <h2>The regulatory part, which is genuinely remarkable</h2>
      <p>
        In October 2023 the EU brought in Regulation (EU) 2023/2055, which
        restricts intentionally added synthetic polymer microparticles across
        the whole market. It did not switch everything off at once. It set
        staggered deadlines by category, and if you lay them out in order, one
        category is a clear outlier.
      </p>

      <BlogComparisonTable
        caption="Transition periods under REACH Annex XVII entry 78, as adopted in Regulation (EU) 2023/2055"
        columns={[
          { key: 'cat', label: 'Category' },
          { key: 'date', label: 'Must comply by' },
          { key: 'years', label: 'Time given' },
        ]}
        rows={[
          { cat: 'Microbeads in rinse-off exfoliants, and loose glitter', date: '17 October 2023', years: 'Immediate' },
          { cat: 'Rinse-off products — shampoo, shower gel, face wash', date: '17 October 2027', years: '4 years' },
          { cat: 'Leave-on products — moisturiser, sunscreen, serum', date: '17 October 2029', years: '6 years' },
          { cat: 'Lip products, nail products and make-up', date: '17 October 2035', years: '12 years' },
        ]}
      />

      <p>
        A face scrub had four years. A body lotion had six.{' '}
        <strong>A bottle of nail polish has twelve, and will be among the last
        consumer products in Europe that may still legally contain
        intentionally added microplastics.</strong> The regulation is candid
        about the reason — colour cosmetics are technically the hardest to
        reformulate, because in polish the polymer is not an additive, it is the
        thing that makes the film. Take out the acrylate copolymer and you do
        not have a slightly different polish; you have a liquid that does not
        set.
      </p>
      <p>
        And there is a detail inside the exemption that is worth knowing about
        now, because it will be visible on shelves within five years. From{' '}
        <strong>17 October 2031</strong> until the 2035 cut-off, those products
        may continue to be sold only if they carry a specific statement.
        Verbatim from the regulation:
      </p>
      <p className="border-l-4 border-forest/40 pl-4 italic">
        &ldquo;From 17 October 2031 until 16 October 2035 suppliers of products
        referred to in paragraph 6, point (c), containing synthetic polymer
        microparticles shall provide the following statement: &lsquo;This
        product contains microplastics.&rsquo;&rdquo;
      </p>
      <p>
        None of this applies in the United States, where there is no federal
        restriction on intentionally added microplastics in cosmetics at all
        beyond the 2015 Microbead-Free Waters Act, which covers rinse-off
        exfoliants and nothing else. The gap is not theoretical:{' '}
        <strong>the identical bottle can be reformulated for a European shelf
        and left unchanged for an American one,</strong> which is exactly the
        pattern that played out with TPO, the gel photoinitiator the EU
        classified as a reproductive toxicant and banned from 1 September 2025
        while it stayed on sale in the US. See{' '}
        <Link href="/blog/eu-microplastics-ban" className="text-forest underline">the EU microplastics ban explained</Link>{' '}
        for the full timeline.
      </p>

      <h2>What is in the bottle, under the names it uses</h2>
      <p>
        The reason nail polish is the most polymer-loaded category in the
        cosmetics aisle is that film-forming <em>is</em> the product. The
        ingredients doing it appear on the label under names that do not read as
        plastic:
      </p>
      <ul>
        <li>
          <strong>Acrylates copolymer</strong> and{' '}
          <strong>styrene/acrylates copolymer</strong> — film formers, and among
          the most commonly used synthetic polymer microparticles in all of
          cosmetics. The styrene version is specific to nail polish and
          eyeliner.
        </li>
        <li>
          <strong>Polyurethane-11</strong> and related polyurethanes — the
          flexible film in longwear and gel-effect formulas.
        </li>
        <li>
          <strong>Polyethylene terephthalate (PET)</strong> — this is what
          glitter is. Not glitter-like: PET film, metallised and cut. A
          Beat the Microbead teardown of a mainstream top coat identified
          exactly this.
        </li>
        <li>
          <strong>Nitrocellulose</strong> — the classic lacquer base, and worth
          separating out honestly. It is a chemically modified{' '}
          <em>natural</em> polymer rather than a synthetic one, so it sits
          outside the microplastics definition even though it forms the film.
          Not everything in the bottle is a microplastic and we are not going to
          pretend otherwise.
        </li>
      </ul>

      <BlogAppCTA
        headline="Check a polish before you buy it"
        sub="Scan the ingredient list on any polish, gel, base or top coat with MicroPlastics to see which film formers are synthetic polymers, and get a 0–100 score for the formula."
      />

      <h2>Who is actually exposed</h2>
      <p>
        This is the part that reframes the whole topic, and it deserves to be
        said plainly.{' '}
        <strong>A client sits in that air for maybe two hours a month. A nail
        technician breathes it for forty hours a week, standing directly over
        the drill, in the breathing zone where the plume is densest.</strong>{' '}
        The 67,567-particles-a-year figure is an occupational exposure estimate,
        not a customer one.
      </p>
      <p>
        The study also found what made it worse, and the list is not what you
        would guess. Air conditioning{' '}
        <em>increased</em> measured microplastic concentrations rather than
        clearing them — recirculating air moves settled particles back into
        suspension. Plastic ceiling and flooring materials added to the load.
        So did the number of people in the room, and the number of treatments
        running at once. What was <em>not</em> on the list of mitigations is a
        surgical mask, which is not rated for particles in this size range.
      </p>
      <p>
        The equipment that works is unglamorous and already exists: a{' '}
        <strong>source-capture extractor built into the table</strong>, pulling
        dust down and away at the hand rather than filtering the room after the
        fact, plus genuine outdoor-air ventilation rather than recirculation.
        If you want one question to ask a salon, it is not about the polish
        brand. It is whether the desks have downdraft extraction.
      </p>

      <h2>What to actually do</h2>
      <ul>
        <li>
          <strong>Pick a salon by its ventilation, not its price list.</strong>{' '}
          Downdraft extraction at each station and a room that does not smell of
          monomer. This is the whole ballgame for the airborne fraction.
        </li>
        <li>
          <strong>If you are a technician, this is occupational, not
          cosmetic.</strong> Source capture at the hand, an N95 rather than a
          surgical mask, and fresh-air ventilation instead of recirculated air
          conditioning.
        </li>
        <li>
          <strong>Prefer soak-off to drilling where the service allows
          it.</strong> Acetone removal moves the plastic into a bin instead of
          into the air you are sitting in. It is worse for the drain and better
          for your lungs, and lungs are the one with a measurement behind it.
        </li>
        <li>
          <strong>Reusable press-ons are the genuinely low-shed option.</strong>{' '}
          No drilling, no dust, and the set leaves in one piece. If you were
          expecting the answer to be a &ldquo;clean&rdquo; polish brand, it is
          not.
        </li>
        <li>
          <strong>Skip the glitter top coat if the polish choice is the one you
          want to make.</strong> Glitter is PET film. It is the least ambiguous
          microplastic in the entire nail category.
        </li>
        <li>
          <strong>Do not rinse gel flakes or dust down the sink.</strong> Wipe
          the table into a bin. Nothing at a wastewater plant is catching a
          50-micrometre acrylic fragment.
        </li>
        <li>
          <strong>Read the label for copolymers, not for buzzwords.</strong>{' '}
          &ldquo;10-free&rdquo; and &ldquo;non-toxic&rdquo; refer to solvents
          and plasticisers, and say nothing at all about the film former. Those
          claims and the microplastics question do not overlap.
        </li>
      </ul>

      <p>
        For the wider cosmetics picture see{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-forest underline">microplastics in cosmetics</Link>{' '}
        and{' '}
        <Link href="/blog/worst-microplastic-ingredients" className="text-forest underline">the worst microplastic ingredients to look for</Link>.
        For the other everyday plastic almost nobody audits, see{' '}
        <Link href="/blog/do-contact-lenses-shed-microplastics" className="text-forest underline">do contact lenses shed microplastics</Link>.
        For what you are breathing at home rather than at the salon, see{' '}
        <Link href="/blog/microplastics-in-the-air-at-home" className="text-forest underline">microplastics in the air at home</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Is nail dust microplastic?',
            answer: 'Yes. Acrylic nails are polymethyl methacrylate set with a liquid methacrylate monomer, so the powder produced when an electric file grinds them down is fragmented plastic. A study of nail salon air published in Science of the Total Environment found acrylic was the most common airborne polymer at 27%, ahead of rubber at 21% and polyurethane at 13%, with most particles being fragments smaller than 50 micrometres.',
          },
          {
            question: 'How many microplastics do you breathe in a nail salon?',
            answer: 'The only peer-reviewed measurement found an average of 46 ± 55 microplastic particles per cubic metre of indoor air, giving an estimated inhalation exposure of 67,567 ± 81,782 particles per year. That is an occupational figure for someone working in the salon, not for a client visiting monthly. The variation is very wide because salons differ enormously in ventilation and treatment volume.',
          },
          {
            question: 'Do gel nails shed microplastics?',
            answer: 'Gel polish is a cured acrylate film, so yes, but the route depends on removal. Soft gel is soaked off in acetone and scraped, which sends flakes into a bowl and often down a drain. Hard or builder gel does not dissolve and must be filed off, which creates the same airborne dust as acrylic. The airborne route is the one with a measurement behind it and the one that reaches your lungs.',
          },
          {
            question: 'Are press-on nails better or worse for microplastics?',
            answer: 'Better, which surprises most people. Press-ons are moulded in one piece — historically ABS plastic, and increasingly soft UV gel in premium sets — and they are lifted off intact rather than drilled or dissolved. No drilling means no respirable dust, and the set goes into a bin as a whole object. The manicure that looks the most plastic is the one that sheds the least.',
          },
          {
            question: 'Why does nail polish have until 2035 under the EU microplastics ban?',
            answer: 'Regulation (EU) 2023/2055 sets staggered transition periods by category under REACH Annex XVII entry 78: 17 October 2023 for microbeads and loose glitter, 17 October 2027 for rinse-off products, 17 October 2029 for leave-on products, and 17 October 2035 for lip, nail and make-up products. Colour cosmetics received the longest period because the synthetic polymer is structural rather than an additive — in polish it is the film former, so removing it means rebuilding the product. From 17 October 2031 those products must carry the statement "This product contains microplastics" to remain on sale until the cut-off.',
          },
          {
            question: 'Does "non-toxic" or "10-free" nail polish mean no microplastics?',
            answer: 'No. Those claims refer to a list of excluded solvents, plasticisers and preservatives — formaldehyde, toluene, dibutyl phthalate and similar — and they say nothing about the film-forming polymer. A 10-free polish can still be built on acrylates copolymer, styrene/acrylates copolymer or polyurethane-11, and can still contain PET glitter. The two questions do not overlap, so you have to read the ingredient list rather than the front of the bottle.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Chen E-Y, Lin K-T, Jung C-C, Chang C-L, Chen C-Y', year: 2022, title: 'Characteristics and influencing factors of airborne microplastics in nail salons (46 ± 55 MPs/m³; 67,567 ± 81,782 MPs/year; acrylic 27%, rubber 21%, polyurethane 13%; fragments <50 µm; air conditioning as an aggravating factor)', publisher: 'Science of the Total Environment 806:151472', url: 'https://doi.org/10.1016/j.scitotenv.2021.151472' },
          { authors: 'European Commission', year: 2023, title: 'Regulation (EU) 2023/2055 amending REACH Annex XVII entry 78 — synthetic polymer microparticles (transition periods: 17 Oct 2027 rinse-off, 17 Oct 2029 leave-on, 17 Oct 2035 lip, nail and make-up; "This product contains microplastics" statement required from 17 Oct 2031)', publisher: 'EUR-Lex', url: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
          { authors: 'Beat the Microbead / Plastic Soup Foundation', year: 2026, title: 'Nail polish: identification of polyethylene terephthalate (PET) glitter particles in a mainstream top coat', publisher: 'beatthemicrobead.org', url: 'https://www.beatthemicrobead.org/project/nail-polish/' },
          { authors: 'US Food and Drug Administration', year: 2026, title: 'Nail care products — methyl methacrylate (MMA) in artificial nails and the ethyl methacrylate monomer systems used instead', publisher: 'FDA', url: 'https://www.fda.gov/cosmetics/cosmetic-products/nail-care-products' },
        ]}
      />
    </BlogPostLayout>
  )
}
